import React from "react";
import { useSelector } from "src/store";

import { selectGroups } from "@dashboard/groups/store/selectors";
import { getNestedGroupsWithIndentation } from "@dashboard/groups/utils";
import { useLicenses } from "@dashboard/licenses/api/helpers";
import { License } from "@devices/types";
import { SelectOption } from "@shared/form/SelectField/SelectField.types";

import { useCreateUniversalLicense } from "./useCreateUniversalLicense";

type CreateUniversalLicenseFn = (groupId: number) => Promise<string | undefined>;
type EnsureUniversalLicenseFn = (groupId: number) => Promise<string | undefined>;

type UseDeviceRegistrationGroup = {
    groupOptions: SelectOption[];
    getUniversalLicensesForGroup: (groupId: number) => License[];
    getUniversalLicenseCodeForGroup: (groupId: number) => string | undefined;
    createUniversalLicense: CreateUniversalLicenseFn;
    ensureUniversalLicense: EnsureUniversalLicenseFn;
    isCreatingLicense: boolean;
    allUniversalLicenses: License[];
};

export const useDeviceRegistrationGroup = (): UseDeviceRegistrationGroup => {
    const allGroups = useSelector(selectGroups);
    const allLicenses = useLicenses();
    // Memoized because every callback below depends on it: as a fresh array each render it changed the identity
    // of `ensureUniversalLicense`, and the enrollment dialog's effect lists that in its dependencies.
    const allUniversalLicenses = React.useMemo(
        () => allLicenses.filter((license) => !!license.universalGroupName),
        [allLicenses],
    );
    const createUniversalLicense = useCreateUniversalLicense({ licenses: allLicenses, universalLicenses: allUniversalLicenses });
    const [isCreatingLicense, setIsCreatingLicense] = React.useState(false);

    const getUniversalLicensesForGroup = React.useCallback(
        (groupId: number) => {
            return allUniversalLicenses.filter((license) => license.universalGroupId === groupId);
        },
        [allUniversalLicenses],
    );

    const getUniversalLicenseCodeForGroup = React.useCallback(
        (groupId: number): string | undefined => {
            const licenses = getUniversalLicensesForGroup(groupId);
            return licenses.length > 0 ? licenses[0].activationCode : undefined;
        },
        [getUniversalLicensesForGroup],
    );

    const ensureUniversalLicense = React.useCallback(
        async (groupId: number): Promise<string | undefined> => {
            const existingLicenseCode = getUniversalLicenseCodeForGroup(groupId);
            if (existingLicenseCode) {
                return existingLicenseCode;
            }

            setIsCreatingLicense(true);
            try {
                const newLicenseCode = await createUniversalLicense(groupId);
                return newLicenseCode;
            } catch (_error) {
                return undefined;
            } finally {
                setIsCreatingLicense(false);
            }
        },
        [getUniversalLicenseCodeForGroup, createUniversalLicense],
    );

    const groupOptions = React.useMemo(() => getNestedGroupsWithIndentation(allGroups), [allGroups]);

    return {
        groupOptions,
        getUniversalLicensesForGroup,
        getUniversalLicenseCodeForGroup,
        createUniversalLicense,
        ensureUniversalLicense,
        isCreatingLicense,
        allUniversalLicenses,
    };
};
